<!--
  Aspedia VueJS Content Creator Component
  *=====
  Import Name:      content-creator
  Name:             Content Creator
  Version:          1.2
  Date:             2018-05-18
  Author:           Torsten Schmidt <torsten@aspedia.net>

  Description:      Multi-widget content creator for homepage building.
  Hints:            To get the current widget, use `this.name`
  Todo:             CSS needs scoping and SCSS structure

  Default Data:     N/A
  Component IMG:    N/A
  Ancestor:         N/A
  Available To:     N/A
  Builder Visible:  False
  Version History:
                    1.     2018-03-15    Torsten Schmidt <torsten@aspedia.net>  Initial Project Prototype.
                    1.1    2018-04-17    Torsten Schmidt <torsten@aspedia.net>  Added Menu component.
                    1.2    2018-04-25    Torsten Schmidt <torsten@aspedia.net>  Added Permissions to use widgets.
                    1.3    2018-04-17    Torsten Schmidt <torsten@aspedia.net>  Added Locking functionality.
                    1.4    2018-04-17    Torsten Schmidt <torsten@aspedia.net>  Added Mobile Menu.
                    1.5    2018-04-18    Torsten Schmidt <torsten@aspedia.net>  Added Message Toasts for Error Messages.
                    1.6    2018-06-07    Torsten Schmidt <torsten@aspedia.net>  Implemented Loading Animation
                    1.7    2019-03-207   Torsten Schmidt <torsten@aspedia.net>  Polished Interfacel, added Media Manager
                    to Icon Card, fixed Menu Editor issue, consolidated loading animations
  *=====
  -->
<template>
  <div>
    <div class="text-left">
      <template v-if="!savedChanges">
        <transition name="slide-fade">
          <div class="preview">
            This is a Preview
          </div>
        </transition>
      </template>

      <!-- Displays Actual Editor -->
      <div
        class="content-creator-editor-container"
      >
        <no-ssr>
          <content-creator-editor
            v-if="canBeUsed"
            :key="2"
            v-model="widget"
            :filestore="filestore"
            :name="name"
            :allowed-widgets="allowedWidgets"
            :edit-permissions="editPermissions"
            :permissions="permissions"
            :menu-style="menuStyle"
            :menu-reduce="menuReduce"
            :menu-selection="menuSelection"
            :is-visible="isVisible"
            :saved-changes="savedChanges"
            :save-pending="savePending"
            @updateBlob="updateBlob"
          />
        </no-ssr>
        <template v-if="isMenu && !menuEmpty">
          <content-menu-editor
            :key="3"
            :widget="widget"
            :menu-style="menuStyle"
            :menu-reduce="menuReduce"
            :menu-selection="menuSelection"
            :menu-direction="menuDirection"
            :saved-changes="savedChanges"
            :menu-container-entry-name="selectedMenuEntry"
          />
        </template>
        <template v-else-if="isMenu">
          <content-none
            v-if="isMenu && menuEmpty"
            :filestore="filestore"
            :widget="widget"
            :name="name"
            :permissions="permissions"
          />
        </template>
      </div>

      <!-- Displays Actual Content -->
      <div
        v-if="isNonMenu"
        :key="widgetType"
        class="content-creator-layout-container"
      >
        <template v-if="hasHeader">
          <content-header
            :header="widgetSettings.header"
            :filestore="filestore"
          />
        </template>
        <component
          :is="layoutComponent"
          v-if="!showPlaceholder"
          :key="4"
          :widget="widget"
          :name="name"
          :filestore="filestore"
        />
        <content-none
          v-else
          :filestore="filestore"
          :widget="widget"
          :name="name"
          :permissions="permissions"
        />
      </div>
    </div>
  </div>
</template>

<script>

// Content Layout
import defaultSettings from './default-settings'

// Plugins

import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'

const contentCarousel = () => import(/* webpackChunkName: 'content-create-common' */ './layouts/content-carousel.vue')
const contentGrid = () => import(/* webpackChunkName: 'content-create-common' */ './layouts/content-grid.vue')

const contentList = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-list.vue')
const contentLinkList = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-link-list.vue')
const contentWysiwygEditor = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-wysiwyg-editor.vue')
const contentAdvancedWysiwygEditor = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-advanced-wysiwyg-editor.vue')
const contentSocialIcons = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-social-icons.vue')
const contentCalendar = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-calendar.vue')

const contentHeader = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-header.vue')

const contentEmpty = () => import(/* webpackChunkName: 'content-creator-empty' */ './layouts/content-empty')
const contentNone = () => import(/* webpackChunkName: 'content-creator-empty' */ './layouts/content-none')

const contentCreatorEditor = () => import(/* webpackChunkName: 'content-creator-editor' */ './content-creator-editor')
const contentMenuEditor = () => import(/* webpackChunkName: 'content-creator-menu' */ './layouts/content-menu-editor.vue')

const contentVimeoVideo = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-vimeo-video.vue')
const contentStandardGrid = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-standard-grid.vue')
const contentFeaturedCarousel = () => import(/* webpackChunkName: 'content-creator-common' */ './layouts/content-featured-carousel.vue')

export default {
  name: 'content-creator',
  components: {
    'content-carousel': contentCarousel,
    'content-standard-grid': contentStandardGrid,
    'content-featured-carousel': contentFeaturedCarousel,
    'content-content-list': contentList,
    'content-link-list': contentLinkList,
    'content-header': contentHeader,
    'content-menu-editor': contentMenuEditor,
    'content-wysiwyg-editor': contentWysiwygEditor,
    'content-advanced-wysiwyg-editor': contentAdvancedWysiwygEditor,
    'content-social-icons': contentSocialIcons,
    'content-calendar': contentCalendar,
    'content-creator-editor': contentCreatorEditor,
    'content-vimeo-video': contentVimeoVideo,
    'content-empty': contentEmpty,
    'content-grid': contentGrid,
    'content-none': contentNone
  },
  props: {
    settings: {
      type: Object,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    allowedWidgets: {
      type: [String, Array],
      default: '*'
    },
    menuStyle: {
      type: String,
      default: 'desktop'
    },
    menuContainerEntryName: {
      type: String,
      default: ''
    },
    menuReduce: {
      type: String,
      default: ''
    },
    menuDirection: {
      type: String,
      default: 'horizontal'
    },
    menuSelection: {
      type: String,
      default: ''
    },
    permissions: {
      type: Object,
      required: false,
      default: () => ({devel: false, edit: false, editMenu: false})
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    filestore: {
      type: [Object, String],
      required: false,
      default: ''
    }
  },
  data() {
    return {
      widget: {},
      systemMenu: {},
      isLoading: false,
      savedChanges: true,
      savePending: false,
      lockToken: {},
      widgetBlockWatch: true,
      showPlaceholder: false,
      testNewWidget: {}
    }
  },
  computed: {
    canBeUsed() {
      const systemRoles = this.$store.getters['config/variable_get']('system_roles')
      return this.$store.getters['user/isDeveloper'] || this.$store.getters['user/hasAnyRole']([systemRoles.crm_staff])
    },
    selectedMenuEntry: function() {
      return (this.menuReduce && this.widgetSettings.selectedMenuEntry) ? this.widgetSettings.selectedMenuEntry[this.menuReduce] : ''
    },
    isNonMenu: function() {
      return (this.widget.widgetType !== 'menu-editor')
    },
    isMenu: function() {
      return (this.widget.widgetType === 'menu-editor')
    },
    layoutComponent: function() {
      return `content-${this.widgetType}`
    },
    widgetType: function() {
      return this.widget.widgetType
    },
    widgetSettings: function() {
      return this.widget.widgetSettings
    },
    hasHeader: function() {
      return this.widget.widgetSettings?.header?.enabled
    },
    menuContent: function() {
      return this.widget.menuContent
    },
    menuEmpty() {
      return !this.menuContent || !this.menuContent['Menu Items'].some(item => item.linkActive)
    },
    editPermissions: function() {
      if (this.isMenu) {
        return this.permissions.editMenu
      }
      else {
        return this.permissions.edit
      }
    },
    carouselCardItems() {
      if (this.widget && this.widget.cardItems) {
        return this.widget.cardItems.filter(item => item.cardType !== 'empty' && item.gridId == null)
      }
      return []
    }
  },
  watch: {
    settings: {
      handler(val) {
        this.widgetBlockWatch = true
        if (Object.entries(val).length !== 0 && val.constructor === Object) {
          let sc = this.savedChanges
          const newObject = JSON.parse(JSON.stringify(merge(this.widget, cloneDeep(defaultSettings), cloneDeep(this.settings))))
          this.$set(this, 'widget', newObject)
          this.savedChanges = sc
        }
      }
    },
    widget: {
      handler(val) {
        this.showPlaceholder = false
        if (!this.$options.components[this.layoutComponent]) {
          this.showPlaceholder = true
        }
        else {
          switch (this.widgetType) {
          case 'none':
          case 'empty':
            this.showPlaceholder = true
            break
          case 'social-icons':
            if (!val.socialLinks || !val.socialLinks.some(link => link.url !== '' && link.url !== 'https://')) {
              this.showPlaceholder = true
            }
            break
          case 'grid':
            if (!val.cardItems || val.cardItems.length === 0) {
              this.showPlaceholder = true
            }
            break
          case 'carousel':
            if (!this.carouselCardItems || this.carouselCardItems.length === 0) {
              this.showPlaceholder = true
            }
            break
          case 'link-list':
            if (!val.linkItems || val.linkItems.length === 0) {
              this.showPlaceholder = true
            }
            break
          case 'content-list':
            if (!val.widgetSettings.listType || !val.widgetSettings.viewType) {
              this.showPlaceholder = true
            }
            else {
              // Short circuit this until all types use the same interface.
              let listType = val.widgetSettings.listType
              const listTypeParts = listType.split(('-'))
              if (listTypeParts.length === 2 && (listTypeParts[0] === 'latest' || listTypeParts[0] === 'featured')) {
                // Not using the updated blob structure. Caress the old structure.
                listType = listTypeParts[1]
              }
              if (!this.$options.components['content-creator-content-list-' + listType]) {
                this.showPlaceholder = true
              }
            }
            break
          case 'advanded-wysiwyg-editor':
          case 'wysiwyg-editor':
            if (!val.widgetSettings.wysiwygContent) {
              this.showPlaceholder = true
            }
            break
          }
        }
        if (!this.widgetBlockWatch) {
          this.savedChanges = false
        }
        this.widgetBlockWatch = false
      },
      deep: true
    }
  },
  created() {
    this.$set(this, 'widget', JSON.parse(JSON.stringify(merge(this.widget, cloneDeep(defaultSettings), cloneDeep(this.settings)))))

    if (this.widget.tab == null) {
      this.$set(this.widget, 'tab', 0)
    }
    if (typeof this.filestore !== 'object' || this.filestore === null) {
      applogger.error(`Filestore on Content Creator: ${this.name} is not configured - received String or null and not filestore`)
      let statusMessage = {
        type: 'error',
        title: 'Content Creator Config Error',
        text: `
          <p>
            It appears that the Content Creator ${this.name} is not configured correctly.
            Received a Filestore name when expecting a Filestore Configuration Object.
          </p>
          <p>Please reload the page and try again, if the problem persists
          please contact the site admin.</p>
        `,
        notification: true,
        showClose: true
      }
      this.$store.dispatch('reportError', statusMessage)
    }
    else if (this.filestore.endpoint === null || this.filestore.filestireID === null) {
      applogger.error(`Filestore on Content Creator: ${this.name} missing endpoint or filestoreID`)
      let statusMessage = {
        type: 'error',
        title: 'Content Creator Config Error',
        text: `
          <p>
            It appears that the Content Creator ${this.name} is not configured correctly.
            Missing FusWS Endpoint or Filestore ID.
          </p>
          <p>Please reload the page and try again, if the problem persists
          please contact the site admin.</p>
        `,
        notification: true,
        showClose: true
      }
      this.$store.dispatch('reportError', statusMessage)
    }
  },
  methods: {
    updateBlob() {
      this.savePending = true
      // Check all custom cards in the blob and strip them off the thumbnail
      if (this.widget.cardItems.length > 0) {
        this.widget.cardItems.forEach(function(item) {
          if (item.fileStore && item.fileStore.file) {
            item.fileStore.file.thumbnail = ''
          }
        })
      }
      // Check all default cards in the blob and strip them off the thumbnail
      let defaultCards = this.widget.defaultSystemCards
      for (let key in defaultCards) {
        if (defaultCards.hasOwnProperty(key)) {
          defaultCards[key].forEach(function(item) {
            if (item.fileStore && item.fileStore.file) {
              item.fileStore.file.thumbnail = ''
            }
          })
        }
      }

      this.$emit('updateBlob', {
        widget: this.widget,
        cb: () => {
          this.savePending = false
          this.savedChanges = true
        }
      })
    },
    releaseLock() {
      // @todo: Re-implement Lock Mechanism
      // this.$aspediaLoader.releaseLock(this.name, this.lockToken[this.name])
      //   .then((result) => {
      //     console.debug('%cContent Creator: %cLocking Status successfully released for: %c' + this.widgetSettings.selectedWidget + ' %c' + this.lockToken[this.name], 'color: #406699', 'color: #444', 'color: green', 'color: blue')
      //     this.lockToken[this.name] = null
      //     this.edit = false
      //   })
      //   .catch((error) => {
      //     this.$eventHub.$emit('statusMessage', {type: 'error', title: 'Widget Lock Status', text: 'This widget could not be unlocked. ' + toString(error), showClose: true})
      //     console.debug('%cContent Creator: %cCould not release locking status for: %c' + this.widgetSettings.selectedWidget + ' ' + this.lockToken[this.name] + ' --> ' + error.toString(), 'color: #406699', 'color: #444', 'color: darkred')
      //   })
    }
  }
}
</script>

<style lang="scss" scoped>
    .preview {
        background-color: #ff9300;
        color: var(--main-white);
        padding: .5em 1em;
        box-shadow: 1px 7px 17px rgba(0, 0, 0, 0.2);
        width: 200px;
        text-align: center;
        right: 230px;
        margin-top: -50px;
        position: absolute;
        z-index: 1;
        border-radius: .25em;
    }
</style>
